
import React from "react";
import { useFormContext } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Settings, Info } from "lucide-react";
import { useDefaultCashbackTemplate } from "@/hooks/useDefaultCashbackTemplate";

interface CampaignCashbackCustomizationProps {
  show: boolean;
}

export function CampaignCashbackCustomization({ show }: CampaignCashbackCustomizationProps) {
  const form = useFormContext();
  const { template: defaultTemplate } = useDefaultCashbackTemplate();
  
  const useCustomCashback = form.watch("use_custom_cashback");

  if (!show) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Settings className="h-5 w-5 text-orange-600" />
          Configurações de Cashback
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mostrar configurações padrão */}
        {defaultTemplate && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Info className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-blue-900">Configurações Padrão Atuais</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                {defaultTemplate.percentage}% de cashback
              </Badge>
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Multiplicador: {defaultTemplate.multiplier}x
              </Badge>
              <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                Validade: {defaultTemplate.expiration_days} dias
              </Badge>
            </div>
          </div>
        )}

        {/* Switch para ativar customização */}
        <FormField
          control={form.control}
          name="use_custom_cashback"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base font-medium">
                  Personalizar Cashback para esta Campanha
                </FormLabel>
                <div className="text-sm text-muted-foreground">
                  Sobrescrever as configurações padrão de cashback apenas para esta campanha
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        {/* Campos de customização (aparecem apenas se o switch estiver ativo) */}
        {useCustomCashback && (
          <div className="space-y-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <h4 className="font-medium text-orange-900 mb-3">Configurações Personalizadas</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="custom_cashback_percentage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Cashback %
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder={defaultTemplate ? defaultTemplate.percentage.toString() : "5"}
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="custom_cashback_multiplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Multiplicador
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        step="1"
                        placeholder={defaultTemplate ? defaultTemplate.multiplier.toString() : "3"}
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="custom_cashback_expiration_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      Validade (dias)
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        step="1"
                        placeholder={defaultTemplate ? defaultTemplate.expiration_days.toString() : "30"}
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="text-sm text-orange-800 bg-orange-100 p-3 rounded-lg">
              <p className="font-medium mb-1">Exemplo com suas configurações:</p>
              <p>
                • Cliente gasta R$ 100 → recebe cupom de R$ {form.watch("custom_cashback_percentage") || defaultTemplate?.percentage || 5}<br/>
                • Para usar o cupom, precisa gastar no mínimo R$ {((form.watch("custom_cashback_percentage") || defaultTemplate?.percentage || 5) * (form.watch("custom_cashback_multiplier") || defaultTemplate?.multiplier || 3)).toFixed(2)}<br/>
                • Cupom expira em {form.watch("custom_cashback_expiration_days") || defaultTemplate?.expiration_days || 30} dias
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
